import React from 'react';
import { WorkspaceProfile, ApplicabilityDecision } from '../types/decision';
import { ControlObligation, StatutoryProvision } from '../types/obligation';
import { StatutoryExceptionRecord } from '../types/exception';
import { formatCitation } from '../utils/citationFormatter';
import { getGovernmentLinkage } from '../utils/governmentDocumentLookup';
import governmentDocumentsData from '../data/government-documents.json';
import penaltySchedule from '../data/penalty-schedule.json';
import {
  Printer,
  X,
  ShieldCheck,
  Building,
  Building2,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Scale,
  FileCheck,
  ExternalLink,
  BookOpen,
  ShieldAlert,
  Users,
  Shield,
  Globe,
  Mail,
  Phone,
  MapPin,
  Award,
  FileText,
  Calendar,
  Sliders,
  Check
} from 'lucide-react';

interface PrintableAuditReportProps {
  isOpen: boolean;
  onClose: () => void;
  workspace: WorkspaceProfile;
  controls: ControlObligation[];
  provisions: StatutoryProvision[];
  decisions: Record<string, ApplicabilityDecision>;
  exceptions?: StatutoryExceptionRecord[];
}

export default function PrintableAuditReport({
  isOpen,
  onClose,
  workspace,
  controls,
  provisions,
  decisions,
  exceptions = []
}: PrintableAuditReportProps) {
  if (!isOpen) return null;

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const applicableControls = controls.filter(c => decisions[c.id]?.status === 'APPLICABLE');
  const unresolvedControls = controls.filter(c => decisions[c.id]?.status === 'UNRESOLVED');
  const notApplicableControls = controls.filter(c => decisions[c.id]?.status === 'NOT_APPLICABLE');
  const unassessedControls = controls.filter(c => !decisions[c.id] || decisions[c.id]?.status === 'UNASSESSED');

  const total = controls.length;
  const progressPercent = Math.round(((total - unassessedControls.length) / total) * 100);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-slate-950/80 backdrop-blur-xs overflow-hidden print:p-0 print:bg-white print:static print:block print:inset-auto print:overflow-visible">
      
      {/* Container - Bounded to viewport height with clean flex column */}
      <div className="relative w-full max-w-5xl h-[90vh] max-h-[920px] bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col print:h-auto print:max-h-none print:w-full print:border-0 print:shadow-none print:rounded-none print:overflow-visible print:my-0">
        
        {/* Floating Controls Bar (Hidden in Print) */}
        <div className="px-6 py-4 bg-slate-100 dark:bg-slate-800 print:hidden border-b border-slate-200 dark:border-slate-700 flex items-center justify-between shrink-0">
          <div className="text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
            <Printer className="w-4 h-4 text-accent" />
            <span>Executive Compliance Dossier — Print Preview</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white bg-accent hover:bg-accent-dark transition-colors shadow-xs"
            >
              <Printer className="w-3.5 h-3.5" />
              <span>Print / Save PDF</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              title="Close Preview (Esc)"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Printable Report Document Body - Scrollable within container */}
        <div className="p-6 sm:p-10 space-y-8 overflow-y-auto flex-1 text-slate-900 dark:text-slate-100 print:p-0 print:overflow-visible print:text-black print:dark:text-black">
          
          {/* Header */}
          <div className="border-b border-slate-200 pb-6 flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-2.5 py-0.5 rounded text-xs font-mono font-bold bg-slate-900 print:bg-slate-100 text-white print:text-slate-900 border border-slate-300">
                  STATUTORY COMPLIANCE DOSSIER
                </span>
                <span className="text-xs font-mono text-slate-500 font-semibold">
                  DPDP Act 2023 & DPDP Rules 2025
                </span>
                {workspace.is_sdf && (
                  <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-amber-100 text-amber-900 border border-amber-300">
                    SDF Designated (Section 10)
                  </span>
                )}
              </div>
              <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900 print:text-black">
                {workspace.entity_name}
              </h1>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600 print:text-slate-700 font-medium">
                {workspace.trade_brand_name && (
                  <span>Trade Name: <strong className="font-bold text-slate-900 print:text-black">{workspace.trade_brand_name}</strong></span>
                )}
                {workspace.cin_registration && (
                  <span>• CIN: <strong className="font-mono text-slate-900 print:text-black">{workspace.cin_registration}</strong></span>
                )}
                {workspace.gstin && (
                  <span>• GSTIN: <strong className="font-mono text-slate-900 print:text-black">{workspace.gstin}</strong></span>
                )}
                {workspace.pan_tan && (
                  <span>• PAN: <strong className="font-mono text-slate-900 print:text-black">{workspace.pan_tan}</strong></span>
                )}
                {workspace.industry_sector && (
                  <span>• Sector: <span className="font-semibold text-slate-800 print:text-black">{workspace.industry_sector}</span></span>
                )}
              </div>
            </div>

            <div className="text-left md:text-right text-xs font-mono text-slate-500 space-y-1 shrink-0 bg-slate-50 print:bg-slate-50 p-3 rounded-xl border border-slate-200">
              <div><strong className="text-slate-700">Audit Date:</strong> {new Date().toISOString().slice(0, 10)}</div>
              <div><strong className="text-slate-700">Baseline:</strong> 15 Sep 2026 Gazette</div>
              <div><strong className="text-slate-700">Status:</strong> Verified Technical Assessment</div>
              {workspace.corporate_website && (
                <div className="text-[11px] text-accent truncate max-w-[200px]">{workspace.corporate_website}</div>
              )}
            </div>
          </div>

          {/* Section 1: Corporate Entity Details & Statutory Governance Profile */}
          <div className="rounded-2xl border border-slate-200 bg-slate-50/70 print:bg-slate-50 p-5 space-y-5 text-xs">
            
            {/* Header Title */}
            <div className="flex items-center justify-between border-b border-slate-200/80 pb-2.5">
              <div className="font-extrabold text-slate-900 print:text-black text-xs uppercase tracking-wider flex items-center gap-2">
                <Building2 className="w-4 h-4 text-accent" />
                <span>1. Corporate Entity Profile & Statutory Governance Scope</span>
              </div>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-200/70 text-slate-700 font-bold">
                Section 2(i) Data Fiduciary
              </span>
            </div>

            {/* Part A: Corporate Identification & Registration Directory */}
            <div className="bg-white print:bg-white rounded-xl border border-slate-200 p-3.5 space-y-2.5">
              <div className="text-[10px] uppercase font-mono font-bold text-slate-500 tracking-wider flex items-center gap-1.5">
                <Building className="w-3.5 h-3.5 text-slate-400" />
                <span>Corporate Identification & Registered Office</span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px]">
                <div>
                  <span className="text-slate-500 block text-[10px] uppercase font-mono">Entity Constitution</span>
                  <span className="font-semibold text-slate-900 print:text-black">{workspace.entity_type || 'Private Limited Company'}</span>
                </div>
                <div>
                  <span className="text-slate-500 block text-[10px] uppercase font-mono">Date of Incorporation</span>
                  <span className="font-semibold text-slate-900 print:text-black">{workspace.incorporation_date || '—'}</span>
                </div>
                <div>
                  <span className="text-slate-500 block text-[10px] uppercase font-mono">Corporate Email</span>
                  <span className="font-mono text-slate-900 print:text-black truncate block">{workspace.corporate_email || '—'}</span>
                </div>
                <div>
                  <span className="text-slate-500 block text-[10px] uppercase font-mono">Corporate Phone</span>
                  <span className="font-mono text-slate-900 print:text-black">{workspace.corporate_phone || '—'}</span>
                </div>
                <div className="sm:col-span-2">
                  <span className="text-slate-500 block text-[10px] uppercase font-mono">Registered Corporate Office</span>
                  <span className="text-slate-800 print:text-black leading-relaxed block">{workspace.registered_address || '—'}</span>
                </div>
                <div className="sm:col-span-2">
                  <span className="text-slate-500 block text-[10px] uppercase font-mono">Principal Operating Headquarters</span>
                  <span className="text-slate-800 print:text-black leading-relaxed block">{workspace.headquarters_address || workspace.registered_address || '—'}</span>
                </div>
              </div>
            </div>

            {/* Part B: Statutory Governance Officers */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* DPO */}
              <div className="bg-white print:bg-white rounded-xl border border-slate-200 p-3 space-y-1.5 text-[11px]">
                <div className="flex items-center gap-1.5 font-bold text-slate-900 print:text-black text-xs">
                  <ShieldCheck className="w-3.5 h-3.5 text-purple-600" />
                  <span>Data Protection Officer</span>
                </div>
                <div className="text-[10px] font-mono text-purple-700 font-semibold">DPDP Act Section 10(2)(a)</div>
                <div className="pt-1">
                  <div className="font-bold text-slate-900 print:text-black">{workspace.dpo_name || 'Adv. Rajesh Sharma'}</div>
                  <div className="text-[10px] text-slate-500">{workspace.dpo_designation || 'Data Protection Officer'}</div>
                  <div className="text-[10px] font-mono text-slate-600 truncate mt-0.5">{workspace.dpo_email || 'dpo@company.com'}</div>
                  {workspace.dpo_phone && <div className="text-[10px] font-mono text-slate-600">{workspace.dpo_phone}</div>}
                  {workspace.dpo_office_address && <div className="text-[10px] text-slate-400 truncate">{workspace.dpo_office_address}</div>}
                </div>
              </div>

              {/* Grievance Officer */}
              <div className="bg-white print:bg-white rounded-xl border border-slate-200 p-3 space-y-1.5 text-[11px]">
                <div className="flex items-center gap-1.5 font-bold text-slate-900 print:text-black text-xs">
                  <Users className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Grievance Redressal</span>
                </div>
                <div className="text-[10px] font-mono text-emerald-700 font-semibold">DPDP Act Section 8(10)</div>
                <div className="pt-1">
                  <div className="font-bold text-slate-900 print:text-black">{workspace.grievance_officer_name || 'Priya Iyer'}</div>
                  <div className="text-[10px] text-slate-500">{workspace.grievance_officer_designation || 'Head of Grievance Redressal'}</div>
                  <div className="text-[10px] font-mono text-slate-600 truncate mt-0.5">{workspace.grievance_officer_email || 'grievance@company.com'}</div>
                  {workspace.grievance_officer_phone && <div className="text-[10px] font-mono text-slate-600">{workspace.grievance_officer_phone}</div>}
                  {workspace.grievance_portal_url && <div className="text-[10px] text-accent truncate">{workspace.grievance_portal_url}</div>}
                </div>
              </div>

              {/* Authorized Signatory */}
              <div className="bg-white print:bg-white rounded-xl border border-slate-200 p-3 space-y-1.5 text-[11px]">
                <div className="flex items-center gap-1.5 font-bold text-slate-900 print:text-black text-xs">
                  <Award className="w-3.5 h-3.5 text-blue-600" />
                  <span>Authorized Signatory</span>
                </div>
                <div className="text-[10px] font-mono text-blue-700 font-semibold">Corporate Attestation</div>
                <div className="pt-1">
                  <div className="font-bold text-slate-900 print:text-black">{workspace.authorized_signatory_name || 'Vikramaditya Singhania'}</div>
                  <div className="text-[10px] text-slate-500">{workspace.authorized_signatory_designation || 'Managing Director & CEO'}</div>
                  <div className="text-[10px] font-mono text-slate-600 truncate mt-0.5">{workspace.authorized_signatory_email || 'ceo@company.com'}</div>
                  <div className="text-[10px] text-slate-400 mt-1 italic">Executive Sign-off Authority</div>
                </div>
              </div>
            </div>

            {/* Part C: Regulatory Scope & Processing Scale */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px]">
              <div className="bg-white print:bg-white rounded-xl border border-slate-200 p-3 space-y-1">
                <span className="text-slate-500 block text-[10px] uppercase font-mono">SDF Status (s.10)</span>
                <span className="font-bold text-slate-900 print:text-black block">
                  {workspace.is_sdf ? 'Significant Data Fiduciary' : 'Standard Fiduciary'}
                </span>
                {workspace.is_sdf && workspace.sdf_factors && workspace.sdf_factors.length > 0 && (
                  <div className="text-[10px] text-slate-600 leading-tight pt-0.5">
                    {workspace.sdf_factors.slice(0, 2).join('; ')}
                  </div>
                )}
              </div>

              <div className="bg-white print:bg-white rounded-xl border border-slate-200 p-3 space-y-1">
                <span className="text-slate-500 block text-[10px] uppercase font-mono">Children Data (s.9)</span>
                <span className="font-bold text-slate-900 print:text-black block">
                  {workspace.processes_children_data ? 'In Scope (s.9 Restrictions)' : 'Not In Scope'}
                </span>
                {workspace.processes_children_data && (
                  <div className="text-[10px] text-slate-600 leading-tight pt-0.5">
                    Consent: {workspace.parental_consent_mechanism || 'DigiLocker / Aadhaar'}
                  </div>
                )}
              </div>

              <div className="bg-white print:bg-white rounded-xl border border-slate-200 p-3 space-y-1">
                <span className="text-slate-500 block text-[10px] uppercase font-mono">Cross-Border (s.16)</span>
                <span className="font-bold text-slate-900 print:text-black block">
                  {workspace.has_cross_border_transfers ? 'International Transfers' : 'Domestic Only'}
                </span>
                {workspace.has_cross_border_transfers && (
                  <div className="text-[10px] text-slate-600 leading-tight pt-0.5">
                    {workspace.cross_border_countries || 'MeitY Whitelist Screened'}
                  </div>
                )}
              </div>

              <div className="bg-white print:bg-white rounded-xl border border-slate-200 p-3 space-y-1">
                <span className="text-slate-500 block text-[10px] uppercase font-mono">Demographics & Scale</span>
                <span className="font-bold text-slate-900 print:text-black block">
                  {workspace.estimated_data_principals || '2,500,000+ Principals'}
                </span>
                <div className="text-[10px] text-slate-600 leading-tight pt-0.5">
                  {workspace.total_data_processors_count || 0} Processors in Scope
                  {workspace.uses_consent_managers ? ' • Consent Manager Integrated' : ''}
                </div>
              </div>
            </div>

          </div>

          {/* Executive Summary Metrics */}
          <div className="grid grid-cols-4 gap-3">
            <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 print:bg-slate-50 text-left">
              <div className="text-[10px] font-mono uppercase text-slate-500">Total Obligations</div>
              <div className="text-xl font-bold mt-1 text-slate-900 print:text-black">{total} Controls</div>
              <div className="text-[10px] text-slate-500">{progressPercent}% Assessed</div>
            </div>
            <div className="p-4 rounded-xl border border-emerald-300 bg-emerald-50/40 print:bg-emerald-50 text-left">
              <div className="text-[10px] font-mono uppercase text-emerald-700">Applicable (In-Scope)</div>
              <div className="text-xl font-bold mt-1 text-emerald-800">{applicableControls.length} Controls</div>
              <div className="text-[10px] text-emerald-600">Active Technical Scope</div>
            </div>
            <div className="p-4 rounded-xl border border-amber-300 bg-amber-50/40 print:bg-amber-50 text-left">
              <div className="text-[10px] font-mono uppercase text-amber-700">Exempted (Safe Harbours)</div>
              <div className="text-xl font-bold mt-1 text-amber-800">{notApplicableControls.length} Controls</div>
              <div className="text-[10px] text-amber-600">Section 17 Relief</div>
            </div>
            <div className="p-4 rounded-xl border border-slate-300 bg-slate-50 text-left">
              <div className="text-[10px] font-mono uppercase text-slate-600">Unresolved Items</div>
              <div className="text-xl font-bold mt-1 text-slate-800">{unresolvedControls.length} Controls</div>
              <div className="text-[10px] text-slate-500">Action Required</div>
            </div>
          </div>

          {/* Applicable In-Scope Obligations Table */}
          <div className="space-y-3">
            <h2 className="text-sm font-extrabold uppercase tracking-wider text-slate-800 border-b border-slate-200 pb-1 flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>In-Scope Statutory Control Obligations ({applicableControls.length})</span>
            </h2>

            {applicableControls.length > 0 ? (
              <div className="border border-slate-200 rounded-xl overflow-x-auto text-xs">
                <table className="w-full min-w-[640px] text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-100 print:bg-slate-100 border-b border-slate-200 font-semibold text-slate-700">
                      <th className="py-2.5 px-3 w-20 font-mono">Control ID</th>
                      <th className="py-2.5 px-3">Obligation Title & Statutory Authority</th>
                      <th className="py-2.5 px-3 w-36">Assigned Owner</th>
                      <th className="py-2.5 px-3 w-28 font-mono">Tranche</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 text-slate-800">
                    {applicableControls.map((c) => {
                      const d = decisions[c.id];
                      const govLink = getGovernmentLinkage(c, true);
                      return (
                        <tr key={c.id}>
                          <td className="py-2.5 px-3 font-mono font-bold align-top">{c.id}</td>
                          <td className="py-2.5 px-3 align-top space-y-1">
                            <div className="font-bold">{c.title || c.name}</div>
                            <div className="text-[11px] text-slate-500">{c.scope}</div>
                            {govLink && (
                              <div className="text-[10px] font-mono text-slate-600 print:text-slate-700 bg-slate-50 print:bg-slate-50 border border-slate-200 rounded px-2 py-1 flex items-center justify-between gap-2">
                                <div>
                                  <span className="font-bold text-slate-800">{govLink.shortTitle}</span> ({govLink.gazetteNotification})
                                  {govLink.lineStart && <span> • Lines {govLink.lineStart}–{govLink.lineEnd}</span>}
                                </div>
                                <span className="text-[9px] text-slate-400 font-mono">SHA: {govLink.sha256.slice(0, 10)}...</span>
                              </div>
                            )}
                            {d?.rationale && (
                              <div className="text-[11px] text-slate-600 italic bg-slate-50 p-1.5 rounded">
                                Rationale: {d.rationale}
                              </div>
                            )}
                          </td>
                          <td className="py-2.5 px-3 font-medium align-top">{d?.assigned_owner || c.recommended_owner}</td>
                          <td className="py-2.5 px-3 font-mono text-[11px] align-top">
                            {c.legal_source_records?.[0]?.commencement || '2027-05-13'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-4 rounded-xl border border-dashed border-slate-300 text-xs text-slate-500 italic text-center">
                No controls marked as Applicable yet.
              </div>
            )}
          </div>

          {/* Exempted Controls / Statutory Safe Harbours */}
          {notApplicableControls.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-extrabold uppercase tracking-wider text-amber-800 border-b border-slate-200 pb-1 flex items-center gap-1.5">
                <ShieldAlert className="w-4 h-4 text-amber-600" />
                <span>Documented Statutory Safe Harbours & Exemptions ({notApplicableControls.length})</span>
              </h2>

              <div className="border border-amber-200 rounded-xl overflow-x-auto text-xs bg-amber-50/20">
                <table className="w-full min-w-[640px] text-left border-collapse">
                  <thead>
                    <tr className="bg-amber-100/60 border-b border-amber-200 font-semibold text-amber-900">
                      <th className="py-2.5 px-3 w-20 font-mono">Control</th>
                      <th className="py-2.5 px-3">Obligation Title & Statutory Exemption Basis</th>
                      <th className="py-2.5 px-3 w-36">Approved Counsel</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-amber-200 text-slate-800">
                    {notApplicableControls.map((c) => {
                      const d = decisions[c.id];
                      return (
                        <tr key={c.id}>
                          <td className="py-2.5 px-3 font-mono font-bold text-amber-800 align-top">{c.id}</td>
                          <td className="py-2.5 px-3 align-top space-y-1">
                            <div className="font-bold">{c.title || c.name}</div>
                            <div className="text-[11px] text-slate-700 bg-white print:bg-white p-2 rounded border border-amber-200">
                              {d?.rationale || 'Exempt under Section 17 statutory safe harbour.'}
                            </div>
                            {d?.entity_facts && (
                              <div className="text-[10px] text-slate-500 font-mono">
                                {d.entity_facts}
                              </div>
                            )}
                          </td>
                          <td className="py-2.5 px-3 font-medium align-top">
                            <div>{d?.reviewer_name || 'Legal Counsel'}</div>
                            <div className="text-[10px] font-mono text-slate-400">{d?.review_date}</div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Unresolved Blockers */}
          {unresolvedControls.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-extrabold uppercase tracking-wider text-slate-800 border-b border-slate-200 pb-1 flex items-center gap-1.5">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <span>Unresolved Legal & Fact Blockers ({unresolvedControls.length})</span>
              </h2>

              <div className="border border-slate-200 rounded-xl overflow-x-auto text-xs">
                <table className="w-full min-w-[500px] text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-100 border-b border-slate-200 font-semibold text-slate-700">
                      <th className="py-2.5 px-3 w-20 font-mono">Control</th>
                      <th className="py-2.5 px-3">Issue / Blocked Context</th>
                      <th className="py-2.5 px-3 w-36">Owner</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 text-slate-800">
                    {unresolvedControls.map((c) => {
                      const d = decisions[c.id];
                      return (
                        <tr key={c.id}>
                          <td className="py-2.5 px-3 font-mono font-bold text-amber-800 align-top">{c.id}</td>
                          <td className="py-2.5 px-3 align-top">
                            <div className="font-bold">{c.title}</div>
                            <div className="text-[11px] text-slate-600 mt-0.5">
                              {d?.entity_facts || 'Missing required entity facts or statutory interpretation.'}
                            </div>
                          </td>
                          <td className="py-2.5 px-3 font-medium align-top">{d?.assigned_owner || 'Unassigned'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Statutory Exceptions Register Appendix */}
          {exceptions.length > 0 && (
            <div className="space-y-3 pt-4 border-t border-slate-200">
              <h2 className="text-sm font-extrabold uppercase tracking-wider text-slate-800 border-b border-slate-200 pb-1 flex items-center gap-1.5">
                <Shield className="w-4 h-4 text-amber-600" />
                <span>Appendix: Statutory Safe Harbours Register (Section 17 & Section 3(c)) ({exceptions.length})</span>
              </h2>
              <div className="border border-slate-200 rounded-xl overflow-x-auto text-xs">
                <table className="w-full min-w-[640px] text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-100 print:bg-slate-100 border-b border-slate-200 font-semibold text-slate-700">
                      <th className="py-2 px-3 w-20 font-mono">Exception ID</th>
                      <th className="py-2 px-3 w-28 font-mono">Statutory Ground</th>
                      <th className="py-2 px-3">Title & Legal Justification</th>
                      <th className="py-2 px-3 w-32">Approved By</th>
                      <th className="py-2 px-3 w-20 font-mono">Controls</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 text-slate-800">
                    {exceptions.map((ex) => (
                      <tr key={ex.id}>
                        <td className="py-2 px-3 font-mono font-bold text-amber-700 align-top text-[11px]">
                          {ex.id}
                        </td>
                        <td className="py-2 px-3 font-mono font-bold text-slate-800 align-top text-[11px]">
                          {ex.statutory_ground}
                        </td>
                        <td className="py-2 px-3 align-top space-y-0.5">
                          <div className="font-semibold text-slate-900 print:text-black">{ex.title}</div>
                          <div className="text-[10px] text-slate-600 leading-relaxed">{ex.justification}</div>
                        </td>
                        <td className="py-2 px-3 align-top text-[11px]">
                          <div className="font-medium text-slate-800">{ex.approved_by}</div>
                          <div className="text-[10px] font-mono text-slate-400">{ex.approval_date}</div>
                        </td>
                        <td className="py-2 px-3 font-mono text-[11px] align-top text-slate-600 font-bold">
                          {(ex.mapped_obligation_ids || []).length}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Authoritative Government Gazette Register Appendix */}
          <div className="space-y-3 pt-4 border-t border-slate-200">
            <h2 className="text-sm font-extrabold uppercase tracking-wider text-slate-800 border-b border-slate-200 pb-1 flex items-center gap-1.5">
              <BookOpen className="w-4 h-4 text-accent" />
              <span>Appendix: Authoritative Government Published Documents ({governmentDocumentsData.length})</span>
            </h2>
            <div className="border border-slate-200 rounded-xl overflow-x-auto text-xs">
              <table className="w-full min-w-[640px] text-left border-collapse">
                <thead>
                  <tr className="bg-slate-100 print:bg-slate-100 border-b border-slate-200 font-semibold text-slate-700">
                    <th className="py-2 px-3 w-28 font-mono">Gazette No.</th>
                    <th className="py-2 px-3">Instrument Title & Authority</th>
                    <th className="py-2 px-3 w-24 font-mono">Gazette Date</th>
                    <th className="py-2 px-3 w-32 font-mono">SHA-256 Checksum</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 text-slate-800">
                  {governmentDocumentsData.map((doc) => (
                    <tr key={doc.id}>
                      <td className="py-2 px-3 font-mono font-bold text-accent align-top text-[11px]">
                        {doc.gazette_notification || doc.short_title}
                      </td>
                      <td className="py-2 px-3 align-top">
                        <div className="font-semibold text-slate-900 print:text-black">{doc.short_title}</div>
                        <div className="text-[10px] text-slate-500">{doc.issuer} • {doc.category_label}</div>
                      </td>
                      <td className="py-2 px-3 font-mono text-[11px] align-top text-slate-600">
                        {doc.publication_date_display}
                      </td>
                      <td className="py-2 px-3 font-mono text-[10px] align-top text-slate-500 truncate max-w-[120px]">
                        {doc.sha256.slice(0, 12)}...
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Statutory Sign-Off & Legal Attestation */}
          <div className="border-t border-slate-200 pt-6 space-y-6 text-xs">
            <div className="p-4 rounded-xl bg-slate-50 print:bg-slate-50 border border-slate-200 text-[11px] leading-relaxed text-slate-700">
              <strong className="text-slate-900 print:text-black block mb-1 font-bold">
                STATUTORY ATTESTATION & LEGAL VERIFICATION STATEMENT
              </strong>
              This Digital Personal Data Protection compliance dossier and applicability register have been prepared in accordance with the provisions of the Digital Personal Data Protection Act, 2023 (Act No. 22 of 2023) and the Digital Personal Data Protection Rules, 2025. The undersigned statutory officers hereby attest that the technical controls, architecture system linkages, statutory safe harbour justifications, and enterprise governance structures documented herein accurately reflect the operational posture of <strong className="text-slate-900 print:text-black">{workspace.entity_name}</strong> {workspace.trade_brand_name ? `(d/b/a ${workspace.trade_brand_name})` : ''} as of {new Date().toISOString().slice(0, 10)}.
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-xs pt-2">
              <div className="space-y-3 p-3 rounded-xl border border-slate-200 bg-white print:bg-white">
                <div className="font-bold text-slate-900 print:text-black">
                  {workspace.dpo_name || 'Adv. Rajesh Sharma'}
                </div>
                <div className="text-[10px] text-slate-500 font-mono">
                  {workspace.dpo_designation || 'Data Protection Officer'} (s.10(2)(a))
                </div>
                <div className="h-10 border-b border-dashed border-slate-400"></div>
                <div className="text-[10px] text-slate-400">DPO Signature & Stamp</div>
              </div>

              <div className="space-y-3 p-3 rounded-xl border border-slate-200 bg-white print:bg-white">
                <div className="font-bold text-slate-900 print:text-black">
                  {workspace.authorized_signatory_name || 'Vikramaditya Singhania'}
                </div>
                <div className="text-[10px] text-slate-500 font-mono">
                  {workspace.authorized_signatory_designation || 'Managing Director & CEO'}
                </div>
                <div className="h-10 border-b border-dashed border-slate-400"></div>
                <div className="text-[10px] text-slate-400">Executive Authority Signature</div>
              </div>

              <div className="space-y-3 p-3 rounded-xl border border-slate-200 bg-white print:bg-white">
                <div className="font-bold text-slate-900 print:text-black">
                  Lead Privacy Counsel
                </div>
                <div className="text-[10px] text-slate-500 font-mono">
                  General Counsel / Legal Reviewer
                </div>
                <div className="h-10 border-b border-dashed border-slate-400"></div>
                <div className="text-[10px] text-slate-400">Legal Reviewer Signature</div>
              </div>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
}
